import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useAuthUser from '../services/useAuthUser';
import { auth } from '../services/firebase';
import SEO from '../components/SEO';

const API_BASE = process.env.REACT_APP_API_URL || '';
const PROMO_STATUS_POLL_MS = 30000;

const CAMPAIGN_COPY = {
  mck_assessment_beta_26: {
    title: 'McKinsey Assessment Beta',
    description: 'Claim limited beta access to the Sea Wolf Solver, Sea Wolf Simulation, and Red Rock Simulation.',
  },
  bcg_casey_beta_26: {
    title: 'BCG Casey Beta',
    description: 'Claim limited beta access to the BCG Casey Simulation.',
  },
};

function getTone(code, ok) {
  if (ok) return 'bg-emerald-50 text-emerald-800 border-emerald-200';
  if (code === 'campaign_closed' || code === 'promo_usage_exhausted') return 'bg-amber-50 text-amber-800 border-amber-200';
  return 'bg-rose-50 text-rose-800 border-rose-200';
}

function toMessage(payload) {
  if (!payload) return 'Unable to load promo status right now.';
  if (payload.ok) return 'Promo access claimed successfully.';
  switch (payload.code) {
    case 'already_claimed':
      return 'You already claimed this promo.';
    case 'campaign_closed':
      return 'This promo is fully claimed.';
    case 'campaign_not_started':
      return 'This promo is not open yet.';
    case 'campaign_expired':
      return 'This promo is no longer available.';
    case 'verified_email_required':
      return 'Please verify your email address before claiming this promo.';
    default:
      return payload.error || 'Unable to claim promo access.';
  }
}

export default function PromoClaimPage() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuthUser();
  const [campaignStatus, setCampaignStatus] = useState(null);
  const [statusError, setStatusError] = useState('');
  const [claimState, setClaimState] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const copy = useMemo(() => CAMPAIGN_COPY[slug] || {
    title: 'Promo Claim',
    description: 'Claim limited beta access to a Cognidoor assessment product.',
  }, [slug]);

  useEffect(() => {
    if (!loading && !user) {
      navigate(`/auth?redirect=${encodeURIComponent(`/promo/${slug}`)}`);
    }
  }, [loading, navigate, slug, user]);

  const fetchCampaignStatus = useCallback(async (activeUser) => {
    if (!activeUser || !slug) return;
    try {
      const token = await activeUser.getIdToken();
      const response = await fetch(`${API_BASE}/api/promo/campaigns/${encodeURIComponent(slug)}/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.error || `Failed to load promo status (${response.status})`);
      }
      setCampaignStatus(payload);
      setStatusError('');
    } catch (error) {
      setCampaignStatus(null);
      setStatusError(error.message || 'Unable to load promo status');
    }
  }, [slug]);

  useEffect(() => {
    if (!user || !slug) return undefined;
    void fetchCampaignStatus(user);
    const timer = setInterval(() => {
      void fetchCampaignStatus(user);
    }, PROMO_STATUS_POLL_MS);
    return () => {
      clearInterval(timer);
    };
  }, [fetchCampaignStatus, slug, user]);

  async function handleClaim() {
    if (!auth.currentUser || !slug) return;
    setSubmitting(true);
    setClaimState(null);
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE}/api/promo/claim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ campaign_slug: slug }),
      });
      const payload = await response.json().catch(() => null);
      setClaimState(payload || { ok: false, code: 'unknown_error' });

      await fetchCampaignStatus(auth.currentUser);
    } catch (error) {
      setClaimState({ ok: false, code: 'unknown_error', error: error.message || 'Unable to claim promo access' });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <p className="text-sm text-slate-600">Checking your account…</p>
      </div>
    );
  }

  const closed = campaignStatus?.status && campaignStatus.status !== 'active';
  const claimed = Boolean(campaignStatus?.already_claimed_by_user);

  return (
    <>
      <SEO
        title={`${copy.title} | Cognidoor`}
        description={copy.description}
        url={`/promo/${slug}`}
        noIndex
      />
      <div className="min-h-screen bg-slate-50 px-6 py-16">
        <div className="mx-auto max-w-2xl rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="space-y-4">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Limited Beta Promo</p>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">{copy.title}</h1>
            <p className="text-base text-slate-600">{copy.description}</p>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Seats Remaining</p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">
                {campaignStatus ? campaignStatus.remaining_claims : '—'}
              </p>
              {campaignStatus?.status === 'active' && Number.isFinite(Number(campaignStatus?.remaining_claims)) ? (
                <p className="mt-2 text-xs font-semibold text-amber-700">
                  Live count refreshes every 30s for urgency.
                </p>
              ) : null}
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Claim Rules</p>
              <p className="mt-2 text-sm text-slate-700">One claim per campaign per user. Verified email required. 14-day expiry. 3 uses per included product.</p>
            </div>
          </div>

          {statusError ? (
            <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {statusError}
            </div>
          ) : null}

          {claimState ? (
            <div className={`mt-6 rounded-2xl border px-4 py-3 text-sm ${getTone(claimState.code, claimState.ok)}`}>
              {toMessage(claimState)}
            </div>
          ) : null}

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={handleClaim}
              disabled={submitting || closed || claimed}
              className="inline-flex items-center justify-center rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {claimed ? 'Already claimed' : submitting ? 'Claiming…' : 'Claim promo access'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/account')}
              className="inline-flex items-center justify-center rounded-full border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:text-slate-900"
            >
              Go to account
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
